import { Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import type { AssignmentSource } from "modules/aiMapping/aiAssignmentSources";
import { useMemo } from "react";

const groupForSource = (source: AssignmentSource) =>
  source.kind === "ai_count" ? "Counts" : "Areas";

export default function SourcePicker({
  value,
  sources,
  disabled,
  onChange,
}: {
  value: string;
  sources: AssignmentSource[];
  disabled?: boolean;
  onChange: (sourceId: string) => void;
}) {
  const orderedSources = useMemo(
    () =>
      sources
        .slice()
        .sort(
          (a, b) =>
            groupForSource(a).localeCompare(groupForSource(b)) || a.label.localeCompare(b.label)
        ),
    [sources]
  );
  const selected = orderedSources.find((source) => source.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      options={orderedSources}
      value={selected}
      groupBy={groupForSource}
      getOptionLabel={(source) => `${source.label} · ${source.detail}`}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      onChange={(_, source) => onChange(source?.id ?? "")}
      renderOption={(props, source) => (
        <li {...props} key={source.id}>
          <MDBox
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: source.color,
              mr: 1,
              flexShrink: 0,
            }}
          />
          <MDBox>
            <UiTypography uiVariant="caption" sx={{ fontWeight: 700, display: "block" }}>
              {source.label}
            </UiTypography>
            <UiTypography uiVariant="caption" sx={{ color: "#667085", display: "block" }}>
              {source.detail}
            </UiTypography>
          </MDBox>
        </li>
      )}
      renderInput={(params) => <TextField {...params} placeholder="Add measurement…" />}
      componentsProps={{ popper: { style: { zIndex: 1600 } } }}
    />
  );
}
